import React from 'react';
import { Box, Center, Stack, Text, Flex, Button } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import theme from 'theme';
import useLang from 'lang';
import SimpleLayout from 'layouts/SimpleLayout';
import { IconChevronLeft } from '@tabler/icons-react';
import P2Medium from 'theme/components/Typography/P2Medium';

interface ErrorStateProps {
  error?: string | null;
  title?: string;
  message?: string;
  onRetry?: () => void;
  backPath?: string;
  backLabel?: string;
  showBackButton?: boolean;
}

/**
 * Error state component for displaying error messages with retry functionality
 */
const ErrorState: React.FC<ErrorStateProps> = ({
  error,
  title,
  message,
  onRetry,
  backPath = '/',
  backLabel = 'Back',
  showBackButton = true,
}) => {
  const { _t } = useLang();

  const defaultTitle = error ? _t('Error Loading Request') : _t('Request Not Found');
  const defaultMessage = error
    ? _t('We encountered an issue while loading the request details. Please try again.')
    : _t(
        'The requested helpdesk ticket could not be found. It may have been removed or you may not have permission to view it.'
      );

  return (
    <SimpleLayout>
      <Box p="1rem">
        {showBackButton && (
          <Flex direction="row" align="center" mb="2rem">
            <Link to={backPath} style={{ textDecoration: 'none', display: 'flex', alignContent: 'center' }}>
              <IconChevronLeft size="1.5rem" color={theme.colors?.gray?.[9]} />
              <P2Medium color={theme.colors?.gray?.[9]}>{_t(backLabel)}</P2Medium>
            </Link>
          </Flex>
        )}

        <Center h="50vh">
          <Stack align="center" gap="lg" maw="400px">
            <IconAlertCircle size="3rem" color={theme.colors?.primary?.[0]} />
            <Stack align="center" gap="sm">
              <Text size="xl" fw={600} color={theme.colors?.gray?.[12]} ta="center">
                {title || defaultTitle}
              </Text>
              <Text size="md" color={theme.colors?.gray?.[8]} ta="center" lh={1.5}>
                {message || defaultMessage}
              </Text>
            </Stack>

            <Flex gap="sm" mt="md">
              {onRetry && (
                <Button color="primary.0" variant="outline" leftSection={<IconRefresh size="1rem" />} onClick={onRetry}>
                  {_t('Try Again')}
                </Button>
              )}
              <Button component={Link} to={backPath} variant="filled" color="primary.0">
                {_t(backLabel)}
              </Button>
            </Flex>
          </Stack>
        </Center>
      </Box>
    </SimpleLayout>
  );
};

export default ErrorState;
